import { $fetch } from "ofetch";
import { parseCookies, getRequestIP, deleteCookie } from "h3";

const CSRF_COOKIE = "ONLINE-XSRF-TOKEN";
const CSRF_HEADER = "X-XSRF-TOKEN";
const REFERRER_COOKIE = "client_source_referrer";
const SOURCE_ATTACH_ENDPOINTS = [
  "new-booking",
  "new-membership",
  "new-gift-card",
  "new-product-order",
];

/** Shown to the visitor as a toast by `onResponseError`. */
const TOASTED_STATUSES = [400, 422, 429];
/** Shown to the visitor as the full error page by `onResponseError`. */
const ERROR_PAGE_STATUSES = [500, 404, 403];

/**
 * Whether `$larafetch` has already shown this failure to the visitor, as a
 * toast or as the error page.
 *
 * Callers that would otherwise rethrow can use this to stay quiet: the visitor
 * has their answer, and rethrowing only turns an expected outcome — a wrong OTP,
 * a daily SMS limit — into an unhandled rejection that Sentry records as a
 * crash. Reads the same lists `onResponseError` uses, so the two cannot drift.
 *
 * A 419 only counts once its single retry is spent (`retry === 0`): that is
 * the one point `onResponseError` toasts it. Before then the request is still
 * being retried with a fresh token, and nothing has been shown.
 *
 * A request that never got a response counts only when `onRequestError`
 * raised the connection toast for it — in the browser, on the final attempt.
 *
 * False for anything nothing was shown for: a status the caller opted out of
 * with `ignoreErrorStatuses`, a 401, or a request that failed without a
 * response where no toast was raised (on the server, say).
 */
export function wasShownToVisitor(error: unknown): boolean {
  const failure = error as {
    response?: { status?: number };
    options?: { ignoreErrorStatuses?: number[]; retry?: number; networkFailureShown?: boolean };
  } | null;
  const status = failure?.response?.status;

  if (status === undefined) {
    return failure?.options?.networkFailureShown === true;
  }

  if (failure?.options?.ignoreErrorStatuses?.includes(status)) {
    return false;
  }

  if (status === 419) {
    return failure?.options?.retry === 0;
  }

  return TOASTED_STATUSES.includes(status) || ERROR_PAGE_STATUSES.includes(status);
}

export const $larafetch = $fetch.create({
  credentials: "include",
  async onRequest({ request, options }) {
    const config = useRuntimeConfig();
    const { backendUrl, frontendUrl } = config.public;

    const event = typeof useEvent === "function" ? useEvent() : null;

    let token = event
      ? parseCookies(event)[CSRF_COOKIE]
      : useCookie(CSRF_COOKIE).value;

    if (
      import.meta.client &&
      options.method &&
      ["post", "delete", "put", "patch"].includes(options.method.toLowerCase())
    ) {
      token = await initCsrf(options.signal || undefined);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let headers: any = {
      accept: "application/json",
      "Accept-Language": useNuxtApp().$i18n.locale.value,
      ...options?.headers,
      ...(token && { [CSRF_HEADER]: token }),
    };

    if (import.meta.server) {
      const cookieString = event
        ? event.headers.get("cookie")
        : useRequestHeaders(["cookie"]).cookie;

      // Forward the real client IP so the API can geolocate the visitor
      // (otherwise it only sees the Nuxt server's IP on SSR requests).
      const forwardedHeaders = useRequestHeaders([
        "x-forwarded-for",
        "x-real-ip",
      ]);
      const clientIp =
        (event && getRequestIP(event, { xForwardedFor: true })) ||
        forwardedHeaders["x-forwarded-for"] ||
        forwardedHeaders["x-real-ip"];

      const { apiAuthUser, apiAuthPass } = config;
      const basicAuth =
        apiAuthUser && apiAuthPass
          ? `Basic ${Buffer.from(`${apiAuthUser}:${apiAuthPass}`).toString(
              "base64"
            )}`
          : undefined;

      headers = {
        ...headers,
        ...(cookieString && { cookie: cookieString }),
        ...(clientIp && {
          "x-forwarded-for": clientIp,
          "x-real-ip": clientIp,
        }),
        ...(basicAuth && { authorization: basicAuth }),
        referer: frontendUrl,
      };
    }

    if (isSourceAttachRequest(request, options)) {
      const referrer = event
        ? parseCookies(event)[REFERRER_COOKIE]
        : useCookie(REFERRER_COOKIE).value;

      if (referrer) {
        headers = { ...headers, "X-Client-Referer": referrer };
      }
    }

    options.headers = headers;
    options.baseURL = backendUrl;
  },
  async onRequestError({ error, options }) {
    if (error.name === "AbortError") {
      return;
    }

    /**
     * The request got no response at all — the connection dropped, or the
     * device is offline. Nothing else tells the visitor: `onResponseError`
     * only runs when there is a response, so a tap on "confirm" used to end in
     * silence mid-booking.
     *
     * Browser only: on the server this is our own connection to the API, not
     * the visitor's, and a toast raised during SSR would be sent down with the
     * page. And only on the last attempt: ofetch calls this hook before it
     * decides to retry, and a toast for a request that then succeeds would be
     * wrong. The flag lets `wasShownToVisitor` treat it like any other failure
     * the visitor has already seen.
     */
    if (import.meta.client && !willRetryNetworkFailure(options)) {
      (options as { networkFailureShown?: boolean }).networkFailureShown = true;
      toastNetworkFailure();
    }
  },
  // @ts-expect-error - ofetch response typing issue
  async onResponseError({ response, options }) {
    if (!response) return;

    const status = response.status;

    if (isLocationClosedResponse(response)) return;

    const ignoredStatuses = (options as { ignoreErrorStatuses?: number[] })
      ?.ignoreErrorStatuses;
    if (ignoredStatuses?.includes(status)) return;

    if (TOASTED_STATUSES.includes(status)) {
      toastError(response);
    }

    if (ERROR_PAGE_STATUSES.includes(status)) {
      return showError({
        statusCode: status,
        statusMessage: (response as { message?: string })?.message,
      });
    }
  },
  async onResponse({ request, response, options }) {
    if (response?.ok && isSourceAttachRequest(request, options)) {
      clearReferrerCookie();
    }

    if (isLocationClosedResponse(response)) {
      const localePath = useLocalePath();
      navigateTo(
        localePath(
          `/${
            (response as { _data: { location: { identifier: string } } })._data
              .location.identifier
          }/closed`
        )
      );
    }
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSourceAttachRequest(request: any, options: any): boolean {
  const requestUrl = typeof request === "string" ? request : request?.url ?? "";

  return (
    options.method?.toLowerCase() === "post" &&
    SOURCE_ATTACH_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint))
  );
}

function clearReferrerCookie(): void {
  const event = typeof useEvent === "function" ? useEvent() : null;

  if (event) {
    deleteCookie(event, REFERRER_COOKIE, { path: "/" });

    return;
  }

  useCookie(REFERRER_COOKIE, { path: "/" }).value = null;
}

function isLocationClosedResponse(
  response: { _data?: { location?: { status?: string } } } | null | undefined,
) {
  return (
    response?._data?.location?.status?.toLowerCase() === "closed"
  );
}

/**
 * Mirrors ofetch 1.4's own retry decision for a request that got no response:
 * it retries only when retries remain — by default one for a GET, none for a
 * write — and treats the missing response as a 500 when checking
 * `retryStatusCodes`, which the default list includes.
 */
export function willRetryNetworkFailure(options: {
  retry?: number | false;
  method?: string;
  retryStatusCodes?: number[];
}): boolean {
  if (options.retry === false) {
    return false;
  }

  const isWrite = ["PATCH", "POST", "PUT", "DELETE"].includes(
    (options.method ?? "GET").toUpperCase()
  );
  const retries = typeof options.retry === "number" ? options.retry : isWrite ? 0 : 1;

  if (retries <= 0) {
    return false;
  }

  return Array.isArray(options.retryStatusCodes)
    ? options.retryStatusCodes.includes(500)
    : true;
}

function toastNetworkFailure() {
  const { t } = useNuxtApp().$i18n;

  useToast().add({
    title: t("alert.network_failure.title"),
    description: t("alert.network_failure.desc"),
    color: "red",
  });
}

function toastError(error: { _data?: { message?: string } }) {
  const toast = useToast();

  toast.add({
    title: error?._data?.message,
    color: "red",
  });
  // icon: "i-approved-warning",
}

async function initCsrf(signal?: AbortSignal) {
  const { backendUrl } = useRuntimeConfig().public;
  const existingToken = useCookie(CSRF_COOKIE).value;

  if (existingToken) return existingToken;

  await $fetch("/sanctum/csrf-cookie", {
    baseURL: backendUrl,
    credentials: "include",
    signal,
  });

  return useCookie(CSRF_COOKIE).value;
}
